
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, PenTool, Puzzle, Brain, Trophy, Star, Gamepad2, GraduationCap, Users } from 'lucide-react';

const GradeSelection = () => {
  const navigate = useNavigate();
  const { grade: urlGrade } = useParams();
  const [selectedGrade, setSelectedGrade] = useState(urlGrade || '');

  const grades = [
    { number: '1', title: 'Grade 1', subtitle: 'Ages 6-7', color: 'from-pink-400 to-red-400' },
    { number: '2', title: 'Grade 2', subtitle: 'Ages 7-8', color: 'from-orange-400 to-pink-400' },
    { number: '3', title: 'Grade 3', subtitle: 'Ages 8-9', color: 'from-yellow-400 to-orange-400' },
    { number: '4', title: 'Grade 4', subtitle: 'Ages 9-10', color: 'from-green-400 to-yellow-400' },
    { number: '5', title: 'Grade 5', subtitle: 'Ages 10-11', color: 'from-teal-400 to-green-400' },
    { number: '6', title: 'Grade 6', subtitle: 'Ages 11-12', color: 'from-blue-400 to-teal-400' },
    { number: '7', title: 'Grade 7', subtitle: 'Ages 12-13', color: 'from-indigo-400 to-blue-400' },
    { number: '8', title: 'Grade 8', subtitle: 'Ages 13-14', color: 'from-purple-400 to-indigo-400' },
    { number: '9', title: 'Grade 9', subtitle: 'Ages 14-15', color: 'from-pink-400 to-purple-400' },
  ];

  const activities = [
    {
      icon: BookOpen,
      title: 'Take Tests',
      description: 'Challenge yourself with subject-specific quizzes and assessments',
      path: `/grade/${selectedGrade}`,
      color: 'from-blue-500 to-cyan-500',
      features: ['Multiple subjects', 'Instant feedback', 'Progress tracking']
    },
    {
      icon: PenTool,
      title: 'Writing Pad',
      description: 'Practice and improve your writing skills with guided exercises',
      path: `/writing/${selectedGrade}`,
      color: 'from-green-500 to-emerald-500',
      features: ['Creative writing', 'Essay practice', 'AI feedback']
    },
    {
      icon: Puzzle,
      title: 'AI Worksheets',
      description: 'Interactive worksheets with puzzles and matching activities',
      path: `/worksheets/${selectedGrade}`,
      color: 'from-purple-500 to-violet-500',
      features: ['Matching games', 'Puzzles', 'Sorting activities']
    },
    {
      icon: Gamepad2,
      title: 'Learning Games',
      description: 'Fun and educational P5.js games for visual learning',
      path: `/games/${selectedGrade}`,
      color: 'from-orange-500 to-red-500',
      features: ['Math games', 'Reading games', 'Interactive learning']
    }
  ];

  const handleGradeSelect = (grade: string) => {
    setSelectedGrade(grade);
    navigate(`/grade/${grade}`);
  };

  const handleActivityClick = (activity: any) => {
    navigate(activity.path);
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleChangeGrade = () => {
    setSelectedGrade('');
    navigate('/dashboard');
  };

  return (
    <div className="max-w-7xl mx-auto">
      {!selectedGrade ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-800 mb-4">
              Select Your Grade Level
            </h1>
            <p className="text-gray-600 text-xl max-w-3xl mx-auto">
              Choose the grade that matches your current level to access tailored
              learning materials and activities designed for your educational journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {grades.map((grade) => (
              <motion.div
                key={grade.number}
                className="cursor-pointer transform transition-all duration-300 hover:scale-105"
                onClick={() => handleGradeSelect(grade.number)}
                whileHover={{ y: -5 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card className="h-full shadow-lg border-2 border-gray-100 hover:border-gray-300 hover:shadow-xl transition-all duration-300">
                  <CardContent className="flex flex-col items-center justify-center p-10">
                    <div
                      className={`w-28 h-28 rounded-full bg-gradient-to-tr ${grade.color} text-white flex items-center justify-center mb-6 shadow-lg`}
                    >
                      <span className="text-4xl font-black">{grade.number}</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-700 mb-2">
                      {grade.title}
                    </h3>
                    <p className="text-gray-500 text-lg">{grade.subtitle}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header with Navigation */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={handleBackToDashboard}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={handleChangeGrade}
                className="flex items-center gap-2"
              >
                <GraduationCap className="h-4 w-4" />
                Change Grade
              </Button>
            </div>
          </div>

          {/* Grade Header */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6 }}
              className={`inline-block p-10 rounded-3xl bg-gradient-to-r ${grades.find(g => g.number === selectedGrade)?.color} text-white shadow-2xl mb-8`}
            >
              <h1 className="text-7xl font-black mb-3">Grade {selectedGrade}</h1>
              <p className="text-2xl opacity-90">
                {grades.find(g => g.number === selectedGrade)?.subtitle}
              </p>
            </motion.div>
            
            <p className="text-gray-600 text-xl max-w-3xl mx-auto">
              Choose your learning activity and start your educational journey! Each activity is designed to enhance your skills and knowledge.
            </p>
          </div>

          {/* Activities Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {activities.map((activity, index) => (
              <motion.div
                key={activity.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:scale-105 border-2 border-gray-200 hover:border-gray-300 group">
                  <CardContent 
                    className="p-8 h-full flex flex-col"
                    onClick={() => handleActivityClick(activity)}
                  >
                    <div className="flex items-start gap-6 mb-6">
                      <div className={`w-20 h-20 rounded-xl bg-gradient-to-r ${activity.color} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow`}>
                        <activity.icon className="h-10 w-10 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold mb-3 text-gray-800">
                          {activity.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed mb-4">
                          {activity.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {activity.features.map((feature, idx) => (
                            <span 
                              key={idx}
                              className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      className={`w-full bg-gradient-to-r ${activity.color} hover:opacity-90 text-white font-semibold py-4 text-lg shadow-lg mt-auto`}
                    >
                      Start Learning
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Stats Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
              <CardContent className="p-10">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold mb-4">Why Choose Our Platform?</h2>
                  <p className="text-indigo-100 text-lg">Designed specifically for Grade {selectedGrade} students</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
                  <div>
                    <Brain className="h-12 w-12 mx-auto mb-4 opacity-90" />
                    <div className="text-3xl font-bold">AI-Powered</div>
                    <div className="text-indigo-100">Smart Learning</div>
                  </div>
                  <div>
                    <Trophy className="h-12 w-12 mx-auto mb-4 opacity-90" />
                    <div className="text-3xl font-bold">Interactive</div>
                    <div className="text-indigo-100">Game-Based Learning</div>
                  </div>
                  <div>
                    <Star className="h-12 w-12 mx-auto mb-4 opacity-90" />
                    <div className="text-3xl font-bold">Adaptive</div>
                    <div className="text-indigo-100">Personalized Experience</div>
                  </div>
                  <div>
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-90" />
                    <div className="text-3xl font-bold">Grade-Specific</div>
                    <div className="text-indigo-100">Age-Appropriate Content</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default GradeSelection;
